import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@ths/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { Provider } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { AuthorizedApolloProvider } from '../components/AuthorizedApolloProvider';
import Header from '../components/Header';
import Loading from '../components/Loading';
import { useHotelMembershipsQuery } from '../generated/graphql';
import { createAppStore } from '../store/store';
import { usePortalTerms } from '../terms/context';

import { CompanySelector } from './CompanySelector';
import { HotelScopeContext, type HotelScope } from './scope';
import { useHotelSession } from './session';
import { HotelDraftChanges } from './unsavedChanges';

function ClientEntry({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-fill text-text">
      <Header />
      <main className="mx-auto max-w-xl px-6 py-12">
        <div className="space-y-5 rounded-md border border-separator bg-surface p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

export function SelectHotelClient() {
  const terms = usePortalTerms();
  const navigate = useNavigate();
  const { confirm } = useHotelSession();
  return (
    <div className="min-h-screen bg-surface text-text">
      <Header />
      <main className="mx-auto max-w-lg px-6 py-10 sm:py-16">
        <CompanySelector
          showIntroduction
          onSelect={(clientId) => {
            confirm(clientId);
            navigate(
              `/clients/${clientId}/${terms?.requiresAcceptance ? 'trips' : 'dashboard'}`,
            );
          }}
        />
      </main>
    </div>
  );
}

function ScopedHotelPortal({
  scope,
  children,
}: {
  scope: HotelScope;
  children: ReactNode;
}) {
  const [store] = useState(createAppStore);
  const { mutationActivity, invalidate } = useHotelSession();
  useEffect(
    () => () => {
      toast.dismiss();
    },
    [],
  );
  return (
    <HotelScopeContext.Provider value={scope}>
      <Provider store={store}>
        <AuthorizedApolloProvider
          clientId={scope.clientId}
          onMutationActivity={mutationActivity}
          onAuthorizationError={invalidate}
        >
          <HotelDraftChanges />
          {children}
        </AuthorizedApolloProvider>
      </Provider>
    </HotelScopeContext.Provider>
  );
}

export function HotelClientGate({ children }: { children: ReactNode }) {
  const { user } = useAuth0();
  const { accessRevision } = useHotelSession();
  // Recheck access before restoring company state after an authorization error
  // or account change. Cached membership data must not reopen the old scope.
  return (
    <ValidatedHotelClientGate key={`${user?.sub}:${accessRevision}`}>
      {children}
    </ValidatedHotelClientGate>
  );
}

function ValidatedHotelClientGate({ children }: { children: ReactNode }) {
  const { clientId } = useParams();
  const { confirmation, confirm } = useHotelSession();
  const { data, loading, error, refetch } = useHotelMembershipsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const membership = data?.hotelMemberships.find(
    (item) => item.clientId === clientId,
  );
  const accessibleClientId =
    !loading && !error ? membership?.clientId : undefined;
  useEffect(() => {
    if (accessibleClientId && confirmation?.clientId !== accessibleClientId)
      confirm(accessibleClientId);
  }, [accessibleClientId, confirmation?.clientId, confirm]);
  if (loading)
    return (
      <div className="flex h-full flex-col justify-center">
        <Loading />
      </div>
    );
  if (error)
    return (
      <ClientEntry>
        <h1 className="text-2xl font-semibold">
          Company access could not be loaded
        </h1>
        <p role="alert">Try again to continue to this page.</p>
        <Button onClick={() => void refetch()}>Try again</Button>
      </ClientEntry>
    );
  if (!membership) return <Navigate to="/select-client" replace />;
  if (confirmation?.clientId !== clientId)
    return (
      <div className="flex h-full flex-col justify-center">
        <Loading />
      </div>
    );
  return (
    <ScopedHotelPortal
      key={`${confirmation.actorId}:${confirmation.clientId}:${confirmation.generation}`}
      scope={{
        ...membership,
        hasMultipleClients: data.hotelMemberships.length > 1,
        stateKey: confirmation.stateKey,
      }}
    >
      {children}
    </ScopedHotelPortal>
  );
}

export function UnsupportedHotelLink() {
  const navigate = useNavigate();
  return (
    <ClientEntry>
      <h1 className="text-2xl font-semibold">
        This link is no longer supported
      </h1>
      <p>
        Open the portal and choose a company to find your reservations or
        requests.
      </p>
      <Button onClick={() => navigate('/select-client')}>Open portal</Button>
    </ClientEntry>
  );
}
